import React, { useState, useRef } from 'react';
import { useTranslation } from '../../../hooks/useTranslation.jsx';
import { useConfirm } from '../../DialogProvider';
import styled from '@emotion/styled';
import { Database, CheckCircle, Upload, FileJson, ChevronDown, ChevronRight, AlertCircle } from 'lucide-react';
import API_ENDPOINTS from '../../../config/api';
import useManualInput, { postManualInput } from '../manualInputs/useManualInput';

const Box = styled.div`
  padding: 24px;
  color: rgba(255, 255, 255, 0.9);
  max-width: 720px;
`;

const StepList = styled.ol`
  margin: 0 0 16px;
  padding-left: 20px;
  font-size: 13px;
  line-height: 1.55;
  color: rgba(255, 255, 255, 0.75);

  li { margin-bottom: 6px; }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 200px;
  margin-top: 8px;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(0, 0, 0, 0.35);
  color: #eee;
  font-family: ui-monospace, monospace;
  font-size: 12px;
  resize: vertical;
`;

const Row = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 12px;
`;

const Button = styled.button`
  padding: 10px 16px;
  border-radius: 8px;
  border: 1px solid ${(p) => (p.$primary ? 'rgba(59, 130, 246, 0.5)' : 'rgba(255, 255, 255, 0.2)')};
  background: ${(p) => (p.$primary ? 'rgba(59, 130, 246, 0.15)' : 'rgba(255, 255, 255, 0.06)')};
  color: ${(p) => (p.$primary ? '#9cf' : 'rgba(255,255,255,0.85)')};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;

  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const GuideToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 16px 0 8px;
  padding: 0;
  border: none;
  background: none;
  color: rgba(255, 255, 255, 0.65);
  font-size: 13px;
  cursor: pointer;
`;

const Guide = styled.div`
  font-size: 12px;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.55);
  padding: 12px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);

  code { font-size: 11px; color: #9cf; }
  table { width: 100%; border-collapse: collapse; margin-top: 8px; }
  th, td { text-align: left; padding: 4px 8px 4px 0; vertical-align: top; }
`;

const HiddenFile = styled.input`
  display: none;
`;

export default function PathwayTreeImportAdmin({
  instanceId,
  locked = false,
  exampleSlug = 'waasa-premade-tree',
  catalogMode = 'trend',
  onApplied,
}) {
  const { t } = useTranslation();
  const confirm = useConfirm();
  // What is on the map now: the import replaces it, so it asks first (#687)
  const { context, reload } = useManualInput(instanceId, 'pathway_tree_import');
  const existingNodes = context?.existingNodes || 0;
  const lastImport = context?.lastImport || null;
  const [jsonText, setJsonText] = useState('');
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const [error, setError] = useState(null);
  const [showGuide, setShowGuide] = useState(false);
  const fileRef = useRef(null);

  const parseLocal = () => {
    try {
      return JSON.parse(jsonText || '{}');
    } catch (e) {
      throw new Error(`JSON ei kelpaa: ${e.message}`);
    }
  };

  const loadExample = async () => {
    setError(null);
    setBusy(true);
    try {
      const res = await fetch(API_ENDPOINTS.WORKSHOP_PATHWAY_TREE_EXAMPLE(exampleSlug));
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || res.statusText);
      setJsonText(JSON.stringify(data.tree, null, 2));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setJsonText(String(reader.result || ''));
      setPreview(null);
      setError(null);
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handlePreview = async () => {
    if (!instanceId || locked) return;
    setBusy(true);
    setError(null);
    setPreview(null);
    try {
      const tree = parseLocal();
      const data = await postManualInput(instanceId, 'pathway_tree_import', 'validate', { tree });
      setPreview(data.preview);
      if (!data.preview?.valid) {
        setError(data.preview.errors?.join(' · ') || 'Validointi epäonnistui');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleApply = async () => {
    if (!instanceId || locked) return;
    // The server refuses to replace an existing map without replaceExisting
    if (existingNodes > 0 && !(await confirm({
      message: t('workshopOrchestrator.pathwayImportReplaceConfirm', { count: existingNodes }),
      destructive: true,
    }))) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const tree = parseLocal();
      let data;
      try {
        data = await postManualInput(instanceId, 'pathway_tree_import', 'apply', { tree, replaceExisting: existingNodes > 0 });
      } catch (err) {
        throw new Error(err.data?.validation?.errors?.join(' · ') || err.message);
      }
      reload();
      onApplied?.();
      setApplyResult(data.seed?.pathwayTreeImport || data.seed);
      setPreview(data.seed ? { valid: true, counts: {
        trends: data.seed.trendsUpserted,
        variables: data.seed.variablesInserted,
        consequenceNodes: data.seed.consequenceNodes,
      } } : null);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const status = applyResult || lastImport;

  return (
    <Box>
      <h3 style={{ margin: '0 0 8px', fontSize: 16 }}>Valmiin kartan tuonti (JSON)</h3>
      <p style={{ margin: '0 0 12px', fontSize: 13, opacity: 0.75 }}>
        Yksi JSON-tiedosto määrittää trendit, muuttujat ja valmiit seurauspuut. Tämä on ainoa lähde —
        älä muokkaa trendejä erikseen instanssin asetuksissa.
      </p>

      <StepList>
        <li>Tuonti täyttää skenaariot ja vaikutuspolut käsin. Jos kulussa on myöhemmin Vaikutuskartta, osallistujat laajentavat tuotua karttaa.</li>
        <li>Lataa esimerkki-JSON, muokkaa offline-tilassa (tai korvaa oikealla sisällöllä ennen työpajaa).</li>
        <li>Liitä JSON alle tai valitse tiedosto → <strong>Esikatsele</strong> → <strong>Kirjoita instanssiin</strong>.</li>
        <li>Jatka: Nykytila &amp; rajaus → Vaikutuskartta (osallistujat voivat laajentaa karttaa).</li>
      </StepList>

      {status && (
        <p style={{ fontSize: 13, color: '#8f8', marginBottom: 12 }}>
          <CheckCircle size={14} style={{ verticalAlign: 'middle' }} />
          {' '}Viimeisin tuonti: {status.trendCount ?? status.trendsUpserted} trendiä,{' '}
          {status.variableCount ?? status.variablesInserted} muuttujaa,{' '}
          {(status.consequenceNodes ?? 0) + (status.starterNodes ?? 0)} solmua kartalla
          {status.at && ` (${new Date(status.at).toLocaleString('fi-FI')})`}.
        </p>
      )}

      <GuideToggle type="button" onClick={() => setShowGuide((v) => !v)}>
        {showGuide ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        JSON-rakenne ({catalogMode})
      </GuideToggle>

      {showGuide && (
        <Guide>
          <p style={{ margin: '0 0 8px' }}>
            Juuriobjekti: <code>version: 1</code>, <code>catalogMode: &quot;trend&quot;</code>,{' '}
            <code>trends[]</code> — jokaisessa trendissä <code>key</code>, <code>title</code>,{' '}
            <code>variables[]</code> ja valinnainen <code>consequences[]</code> -puu.
          </p>
          <table>
            <thead>
              <tr><th>Kenttä</th><th>Kuvaus</th></tr>
            </thead>
            <tbody>
              <tr><td><code>trends[].key</code></td><td>Vakio tunniste (esim. teknologia)</td></tr>
              <tr><td><code>variables[].name</code></td><td>Muuttuja kartalla (syvyys 1)</td></tr>
              <tr><td><code>consequences[].text</code></td><td>Seuraussolmun otsikko (syvyys 2+)</td></tr>
              <tr><td><code>children[]</code></td><td>Sisäkkäiset seuraukset</td></tr>
            </tbody>
          </table>
        </Guide>
      )}

      <Row>
        <Button type="button" onClick={loadExample} disabled={busy || locked}>
          <FileJson size={16} /> Lataa esimerkki-JSON
        </Button>
        <Button type="button" onClick={() => fileRef.current?.click()} disabled={busy || locked}>
          <Upload size={16} /> Valitse tiedosto
        </Button>
      </Row>
      <HiddenFile ref={fileRef} type="file" accept=".json,application/json" onChange={handleFile} />

      <TextArea
        placeholder='{"version":1,"catalogMode":"trend","trends":[...]}'
        value={jsonText}
        onChange={(e) => { setJsonText(e.target.value); setPreview(null); }}
        disabled={busy || locked}
      />

      <Row>
        <Button type="button" onClick={handlePreview} disabled={busy || locked || !jsonText.trim()}>
          Esikatsele
        </Button>
        <Button type="button" $primary onClick={handleApply} disabled={busy || locked || !jsonText.trim()}>
          <Database size={16} /> {busy ? 'Kirjoitetaan…' : 'Kirjoita instanssiin'}
        </Button>
      </Row>

      {preview?.valid && (
        <p style={{ marginTop: 12, fontSize: 13, color: '#9cf' }}>
          <CheckCircle size={14} style={{ verticalAlign: 'middle' }} />
          {' '}{preview.counts?.trends ?? 0} trendiä, {preview.counts?.variables ?? 0} muuttujaa,{' '}
          {preview.counts?.consequenceNodes ?? 0} seuraussolmua (+ juuri ja muuttujat luodaan automaattisesti)
        </p>
      )}

      {error && (
        <p style={{ marginTop: 12, fontSize: 13, color: '#f88' }}>
          <AlertCircle size={14} style={{ verticalAlign: 'middle' }} /> {error}
        </p>
      )}

      {locked && (
        <p style={{ marginTop: 8, fontSize: 12, color: '#ffc107' }}>
          Työpaja on lukittu — avaa lukitus ennen tuontia.
        </p>
      )}
    </Box>
  );
}
